import React, {useEffect, useMemo, useRef, useState} from "react";
import Dialog from "../../base/dialog";
import {Col, Row} from "reactstrap";
import Divider from "@mui/material/Divider";
import Button from "../../base/button/button";
import ReasonNoteSection from "../modify-product-availability/sections/reason-note-section";
import {OutlinedDropdown} from "../../base/dropdown/outlined_dropdown";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";

const reasonNoteFormId = 'reason-note-form-id';

const UpsertBulkAdjustmentProduct = ({open, setOpen, partNumber, initialData, handleUpsertion}) => {
    const [submitting, setSubmitting] = useState(false);
    const [warehouses, setWarehouses] = useState([]);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const initialRender = useRef(true);
    const isMounted = useIsMounted();

    /**
     * Memo version of initial values
     * @type {object}
     */
    const initialValues = useMemo(() => {
        setSelectedWarehouse(initialData?.warehouse);
        return {
            reason: initialData?.reason,
            note: initialData?.note,
        }
    }, [initialData, warehouses])

    /**
     * With initial render of the component, calls the function for getting the list of all warehouses
     */
    useEffect(() => {
        if (initialRender.current) {
            getAllWarehouses().then();
        }
    }, [])

    /**
     * Fetches list of all warehouses available for the operator
     * @return {Promise<void>}
     */
    const getAllWarehouses = async () => {
        const response = await Api.getOperatorWarehouses();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setWarehouses(response?.data ?? []);
        }
    }

    /**
     * Handles selection of a warehouse from the select box:
     * Sets value of the selectedWarehouse in the state and also hides the error message
     * @param warehouse
     */
    const onWarehouseSelected = (warehouse) => {
        setShowErrorMessage(false);
        setSelectedWarehouse(warehouse);
    }

    /**
     * Callback function passed to the reason and note form to handle submission
     * @param values
     */
    const reasonNoteSubmit = async (values) => {
        setSubmitting(true);
        if (!Boolean(selectedWarehouse)) {
            setShowErrorMessage(true);
            setSubmitting(false);
            return;
        }
        await handleUpsertion({
            ...values,
            warehouse: selectedWarehouse,
        })
        if (!isMounted())
            return;
        setSubmitting(false);
        setSelectedWarehouse(null);
    }

    return (
        <>
            <Dialog
                open={open}
                setOpen={setOpen}
                maxWidth={'sm'}
                className={'upsert-bulk-adjustment-product'}
            >
                <Row className='no-gutters align-items-center w-100 transition'>
                    <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                        <p className='text-lg text-color-1 font-weight-semi-bold text-center'>
                            {
                                initialData === null
                                    ? `Add ${partNumber} to Adjustment`
                                    : `Edit ${partNumber} Reason, note, and warehouse`
                            }
                        </p>
                    </Row>
                    <Divider className='divider'/>
                    <div className='my-3 w-100'/>
                    <Row className='no-gutters justify-content-around w-100'>
                        <Row className='no-gutters w-85'>
                            <Col xs={12}>
                                <OutlinedDropdown
                                    classes={{
                                        root: `warehouse-select-box ${showErrorMessage ? 'error' : ''}`
                                    }}
                                    label={'Warehouse'}
                                    onChange={onWarehouseSelected}
                                    value={selectedWarehouse ?? ''}
                                    data={warehouses?.map(e => ({
                                        label: e.name,
                                        value: e,
                                        id: e.id,
                                    })) ?? []}
                                />
                                {showErrorMessage && <p className={'warehouse-selection-helper-error'}>warehouse should be selected</p>}
                            </Col>
                            <Col xs={12}>
                                <ReasonNoteSection
                                    formId={reasonNoteFormId}
                                    submitting={submitting}
                                    onSubmit={reasonNoteSubmit}
                                    initialValues={initialValues}
                                />
                            </Col>
                        </Row>
                        <div className='my-3 w-100'/>
                        <Divider className='divider'/>
                        <Row className='no-gutters justify-content-between align-items-start w-100'>
                            <Button
                                disabled={!open || submitting}
                                variant={'dialog'}
                                color={'primary'}
                                type={'button'}
                                onClick={() => setOpen(false)}
                                borderRadius={'0 8px 0 8px'}
                            >
                                {submitting ? 'loading...' : 'Close'}
                            </Button>
                            <Button
                                disabled={!open || submitting}
                                variant={'dialog'}
                                color={'primary'}
                                type={'submit'}
                                form={reasonNoteFormId}
                                borderRadius={'8px 0 8px 0'}
                            >
                                {submitting ? 'loading...' : initialData !== null ? 'Update' : 'Add'}
                            </Button>
                        </Row>
                    </Row>
                </Row>
            </Dialog>
        </>
    );
}

export default UpsertBulkAdjustmentProduct;
